(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-jm-pdp-2-0]';
  const rootCleanups = new WeakMap();

  const setHeaderCartCount = (cart) => {
    const itemCount = Math.max(0, Number(cart?.item_count) || 0);
    const itemLabel = itemCount === 1 ? '1 item' : `${itemCount} items`;

    document.querySelectorAll('.et-header__cart-count').forEach((container) => {
      const countNode = container.querySelector('span') || container;
      countNode.textContent = String(itemCount);
    });

    document.querySelectorAll('[data-jm-cart-count]').forEach((countNode) => {
      countNode.textContent = String(itemCount);
      const globalCartLink = countNode.closest('.jm-global-header__cart');
      if (globalCartLink) globalCartLink.setAttribute('aria-label', `Cart, ${itemLabel}`);
    });

    const stickyCount = document.querySelector('[data-jm-sticky-cart-count]');
    const stickyCartButton = stickyCount?.closest('[data-jm-open-cart-drawer]') || null;
    if (stickyCount) {
      stickyCount.textContent = String(itemCount);
      stickyCount.hidden = itemCount === 0;
    }
    if (stickyCartButton) {
      stickyCartButton.setAttribute(
        'aria-label',
        itemCount > 0 ? `View cart and checkout, ${itemLabel}` : 'View cart and checkout'
      );
    }
  };

  const initResponsiveLayout = (root) => {
    const top = root.querySelector('.jm-pdp-2-0__top');
    const leftColumn = root.querySelector('.jm-pdp-2-0__left-column');
    const rightColumn = root.querySelector('.jm-pdp-2-0__right-column');
    const infoGrid = root.querySelector('.jm-pdp-2-0-info-grid');
    const purchase = root.querySelector('.jm-pdp-2-0-purchase');
    const delivery = root.querySelector('.jm-pdp-2-0-delivery');
    const fitWarning = root.querySelector('.jm-pdp-2-0-fit-warning');
    const quickFit = root.querySelector('.jm-pdp-2-0-quick-fit');
    const modelNavigation = root.querySelector('.jm-pdp-2-0-model-nav');
    const whyChoose = root.querySelector('.jm-pdp-2-0-why');
    if (!top || !leftColumn || !rightColumn || !purchase || !delivery || !quickFit || !modelNavigation) return null;

    const tabletLayout = window.matchMedia('(min-width: 720px) and (max-width: 1023px)');
    const isBulkModelNavigation = modelNavigation.classList.contains('jm-pdp-2-0-model-nav--bulk');
    const verifiedKitOptions = modelNavigation.querySelectorAll('.jm-pdp-2-0-kit-option');
    const currentKitOptions = modelNavigation.querySelectorAll('.jm-pdp-2-0-kit-option--current');
    const verifiedKitCount = verifiedKitOptions.length;
    const hasSingleCurrentKit = currentKitOptions.length === 1;
    const hasVerifiedAlternativeKit = Boolean(
      modelNavigation.querySelector('.jm-pdp-2-0-kit-option:not(.jm-pdp-2-0-kit-option--current)')
    );
    const useSparseWideLayout = Boolean(infoGrid) && !isBulkModelNavigation && !hasVerifiedAlternativeKit;
    const useFullWidthWhyChoose = Boolean(infoGrid)
      && Boolean(whyChoose)
      && !isBulkModelNavigation
      && hasSingleCurrentKit
      && verifiedKitCount === 3;
    const useRightColumnWhyChoose = Boolean(whyChoose)
      && !isBulkModelNavigation
      && hasSingleCurrentKit
      && verifiedKitCount >= 4;

    const restoreDelivery = () => {
      if (delivery.parentElement === purchase && (!fitWarning || delivery.nextElementSibling === fitWarning)) return;
      if (fitWarning?.parentElement === purchase) {
        purchase.insertBefore(delivery, fitWarning);
      } else {
        purchase.append(delivery);
      }
    };

    const restoreQuickFit = () => {
      if (quickFit.parentElement !== rightColumn || quickFit.previousElementSibling !== purchase) {
        purchase.after(quickFit);
      }
    };

    const placeSparseQuickFit = () => {
      if (!useSparseWideLayout || window.innerWidth < 720 || !infoGrid) return;
      if (quickFit.parentElement !== infoGrid || infoGrid.firstElementChild !== quickFit) {
        infoGrid.prepend(quickFit);
      }
    };

    const placeCountAwareWhyChoose = () => {
      if (!whyChoose || window.innerWidth < 1024) return;

      if (useFullWidthWhyChoose && infoGrid) {
        if (whyChoose.parentElement !== infoGrid || infoGrid.firstElementChild !== whyChoose) {
          infoGrid.prepend(whyChoose);
        }
        return;
      }

      if (useRightColumnWhyChoose) {
        if (whyChoose.parentElement !== rightColumn || quickFit.nextElementSibling !== whyChoose) {
          quickFit.after(whyChoose);
        }
      }
    };

    const restoreServerOrder = () => {
      restoreDelivery();
      restoreQuickFit();
      leftColumn.append(modelNavigation);
      if (whyChoose) leftColumn.append(whyChoose);
    };

    const applyTabletOrder = () => {
      leftColumn.append(modelNavigation);
      if (whyChoose) leftColumn.append(whyChoose);
      purchase.after(delivery);
      delivery.after(quickFit);
    };

    const syncLayout = () => {
      if (tabletLayout.matches) {
        applyTabletOrder();
        placeSparseQuickFit();
        return;
      }

      restoreDelivery();
      restoreQuickFit();

      if (window.innerWidth < 720) {
        rightColumn.append(modelNavigation);
        if (whyChoose) rightColumn.append(whyChoose);
        return;
      }

      leftColumn.append(modelNavigation);
      if (whyChoose) leftColumn.append(whyChoose);
      placeSparseQuickFit();
      placeCountAwareWhyChoose();
    };

    syncLayout();

    if (typeof tabletLayout.addEventListener === 'function') {
      tabletLayout.addEventListener('change', syncLayout);
    } else {
      tabletLayout.addListener(syncLayout);
    }

    return () => {
      if (typeof tabletLayout.removeEventListener === 'function') {
        tabletLayout.removeEventListener('change', syncLayout);
      } else {
        tabletLayout.removeListener(syncLayout);
      }
      restoreServerOrder();
    };
  };

  const initEmptyReviewState = (root) => {
    const reviews = root.querySelector('.jm-pdp-2-0-reviews');
    const plugin = reviews?.querySelector('#opinew-plugin[data-opw-prodreviews]');
    if (!reviews || !plugin) return;

    try {
      const payload = JSON.parse(plugin.getAttribute('data-opw-prodreviews') || '{}');
      const product = payload?.product || {};
      const reviewCount = Number(product.review_number ?? product.rating_number);

      if (Number.isFinite(reviewCount) && reviewCount === 0) {
        reviews.classList.add('jm-pdp-2-0-reviews--empty');
      }
    } catch (_) {
      // Leave Opinew's normal presentation untouched if its metadata is unavailable.
    }
  };

  const initQuantity = (root) => {
    const input = root.querySelector('#Quantity');
    if (!input || input.closest('[data-jm-quantity]')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'jm-pdp-2-0-quantity';
    wrapper.dataset.jmQuantity = '';

    const createButton = (action, label, symbol) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jm-pdp-2-0-quantity__button';
      button.dataset[action] = '';
      button.setAttribute('aria-label', label);
      button.textContent = symbol;
      return button;
    };

    const minus = createButton('jmQuantityMinus', 'Decrease quantity', '−');
    const plus = createButton('jmQuantityPlus', 'Increase quantity', '+');

    input.parentNode.insertBefore(wrapper, input);
    wrapper.append(minus, input, plus);
    const setQuantity = (nextValue) => {
      const minimum = Number.parseInt(input.getAttribute('min'), 10) || 1;
      input.value = String(Math.max(minimum, nextValue));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    minus.addEventListener('click', () => {
      setQuantity((Number.parseInt(input.value, 10) || 1) - 1);
    });

    plus.addEventListener('click', () => {
      setQuantity((Number.parseInt(input.value, 10) || 1) + 1);
    });
  };

  const initCopyLink = (root) => {
    const button = root.querySelector('[data-jm-copy-link]');
    if (!button || button.dataset.jmBound === 'true') return;

    button.dataset.jmBound = 'true';

    button.addEventListener('click', async () => {
      const url = button.dataset.jmShareUrl || window.location.href;
      const status = root.querySelector('[data-jm-copy-status]');
      const label = button.querySelector('[data-jm-copy-label]');

      const showSuccess = () => {
        if (label) label.textContent = 'Copied';
        if (status) status.textContent = 'Product link copied.';

        window.setTimeout(() => {
          if (label) label.textContent = 'Copy link';
          if (status) status.textContent = '';
        }, 1800);
      };

      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
        await navigator.clipboard.writeText(url);
        showSuccess();
      } catch (error) {
        window.prompt('Copy this product link:', url);
      }
    });
  };

  const initMediaControls = (root) => {
    const controls = Array.from(root.querySelectorAll('[data-thumbnail-id]'));
    const viewButton = root.querySelector('[data-jm-view-larger]');
    const mediaRecords = Array.from(root.querySelectorAll('.et-product-single__media-wrapper[data-media-id]'))
      .map((wrapper) => {
        const mediaId = wrapper.dataset.mediaId;
        const control = controls.find((item) => item.dataset.thumbnailId === mediaId) || null;
        const previewImage = control?.querySelector('img') || wrapper.querySelector('[data-jm-media-image]');
        const fullSrc = control?.dataset.jmFullImage
          || previewImage?.dataset.jmFullImage
          || wrapper.querySelector('[data-jm-full-image]')?.dataset.jmFullImage
          || '';

        if (!mediaId || !previewImage || !fullSrc) return null;

        return {
          mediaId,
          previewImage,
          fullSrc
        };
      })
      .filter(Boolean);

    if ((!controls.length && !mediaRecords.length) || root.dataset.jmMediaBound === 'true') return null;

    root.dataset.jmMediaBound = 'true';

    const setCurrent = (currentControl) => {
      controls.forEach((control) => {
        control.classList.toggle('et-active_thumb', control === currentControl);
        control.setAttribute('aria-pressed', control === currentControl ? 'true' : 'false');
      });
    };

    const showMedia = (currentControl) => {
      if (!currentControl) return;

      const mediaId = currentControl.dataset.thumbnailId;
      root.querySelectorAll('.et-product-single__media-wrapper[data-media-id]').forEach((wrapper) => {
        wrapper.classList.toggle('et-hide', wrapper.dataset.mediaId !== mediaId);
      });
      if (viewButton) {
        const record = mediaRecords.find((item) => item.mediaId === mediaId) || null;
        viewButton.hidden = !record;
        if (record) viewButton.dataset.jmMediaId = record.mediaId;
      }
      setCurrent(currentControl);
    };

    const modal = document.getElementById('jm-image-modal');
    const scrollBox = modal?.querySelector('.jm-image-modal__scroll') || null;
    const closeButton = modal?.querySelector('.jm-image-modal__close') || null;
    const nextButton = modal?.querySelector('.jm-image-modal__arrow--next') || null;
    const previousButton = modal?.querySelector('.jm-image-modal__arrow--prev') || null;
    const backdrop = modal?.querySelector('.jm-image-modal__backdrop') || null;

    let modalImages = [];
    let currentIndex = 0;
    let returnFocus = null;
    let inertEntries = [];
    let imageObserver = null;
    let galleryBuilt = false;
    let scrollTimer = null;

    const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    const setBackgroundInert = () => {
      if (!modal || !document.body) return;

      let modalBranch = modal;
      while (modalBranch.parentElement && modalBranch.parentElement !== document.body) {
        modalBranch = modalBranch.parentElement;
      }

      inertEntries = Array.from(document.body.children)
        .filter((element) => element !== modalBranch && !['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT'].includes(element.tagName))
        .map((element) => ({
          element,
          wasInert: element.hasAttribute('inert')
        }));

      inertEntries.forEach(({ element }) => element.setAttribute('inert', ''));
    };

    const restoreBackground = () => {
      inertEntries.forEach(({ element, wasInert }) => {
        if (!wasInert) element.removeAttribute('inert');
      });
      inertEntries = [];
    };

    const loadFullImage = (image, prioritize = false) => {
      const fullSrc = image?.dataset.jmFullSrc;
      if (!image || !fullSrc) return;

      if (prioritize) image.fetchPriority = 'high';
      image.src = fullSrc;
      image.removeAttribute('data-jm-full-src');
      imageObserver?.unobserve(image);
    };

    const buildGallery = () => {
      if (!modal || !scrollBox || galleryBuilt) return;

      scrollBox.innerHTML = '';
      modalImages = mediaRecords.map((record, index) => {
        const sourceImage = record.previewImage;
        const image = document.createElement('img');
        image.className = 'jm-image-modal__img';
        image.src = sourceImage.currentSrc || sourceImage.src;
        image.alt = sourceImage.alt || `Product image ${index + 1}`;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.dataset.index = String(index);
        image.dataset.jmFullSrc = record.fullSrc;

        const width = Number.parseInt(sourceImage.getAttribute('width'), 10);
        const height = Number.parseInt(sourceImage.getAttribute('height'), 10);
        if (width > 0) image.width = width;
        if (height > 0) image.height = height;

        scrollBox.appendChild(image);
        return image;
      });

      if ('IntersectionObserver' in window) {
        imageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) loadFullImage(entry.target);
          });
        }, {
          root: scrollBox,
          rootMargin: '500px 0px'
        });
        modalImages.forEach((image) => imageObserver.observe(image));
      } else {
        modalImages.forEach((image) => loadFullImage(image));
      }

      galleryBuilt = true;
    };

    const updateCurrentIndexFromScroll = () => {
      if (!scrollBox || !modalImages.length) return;

      const boxRect = scrollBox.getBoundingClientRect();
      const boxCenter = boxRect.top + (boxRect.height / 2);
      let closestIndex = currentIndex;
      let smallestDelta = Number.POSITIVE_INFINITY;

      modalImages.forEach((image, index) => {
        const imageRect = image.getBoundingClientRect();
        const delta = Math.abs((imageRect.top + (imageRect.height / 2)) - boxCenter);
        if (delta < smallestDelta) {
          smallestDelta = delta;
          closestIndex = index;
        }
      });

      currentIndex = closestIndex;
    };

    const showImage = (index) => {
      if (!modalImages.length) return;

      currentIndex = (index + modalImages.length) % modalImages.length;
      const image = modalImages[currentIndex];
      loadFullImage(image, true);
      image.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'center'
      });
    };

    const closeModal = ({ restoreFocus = true } = {}) => {
      if (!modal) return;

      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('jm-modal-open');
      restoreBackground();

      const focusTarget = returnFocus;
      returnFocus = null;
      if (restoreFocus && focusTarget?.isConnected) {
        window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    };

    const openModal = (mediaId, opener) => {
      if (!modal || !scrollBox || !closeButton || !mediaRecords.length) return;

      buildGallery();
      const requestedIndex = mediaRecords.findIndex((record) => record.mediaId === String(mediaId));
      currentIndex = requestedIndex >= 0 ? requestedIndex : 0;
      if (modal.classList.contains('is-open')) {
        showImage(currentIndex);
        return;
      }
      returnFocus = opener instanceof HTMLElement ? opener : null;

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('jm-modal-open');
      setBackgroundInert();
      loadFullImage(modalImages[currentIndex], true);

      window.requestAnimationFrame(() => {
        closeButton.focus({ preventScroll: true });
        modalImages[currentIndex]?.scrollIntoView({ behavior: 'auto', block: 'center' });
      });
    };

    const trapFocus = (event) => {
      if (event.key !== 'Tab' || !modal?.classList.contains('is-open')) return;

      const focusable = Array.from(modal.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onModalKeydown = (event) => {
      if (!modal?.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeModal();
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopImmediatePropagation();
        showImage(currentIndex + 1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        event.stopImmediatePropagation();
        showImage(currentIndex - 1);
        return;
      }
      trapFocus(event);
    };

    const onCloseClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeModal();
    };

    const onNextClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      showImage(currentIndex + 1);
    };

    const onPreviousClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      showImage(currentIndex - 1);
    };

    const onBackdropClick = (event) => {
      if (event.target !== backdrop) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeModal();
    };

    const onModalScroll = () => {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(updateCurrentIndexFromScroll, 150);
    };

    if (modal && mediaRecords.length) {
      modal.classList.add('jm-image-modal--pdp-2-0');
      closeButton?.addEventListener('click', onCloseClick);
      nextButton?.addEventListener('click', onNextClick);
      previousButton?.addEventListener('click', onPreviousClick);
      backdrop?.addEventListener('click', onBackdropClick);
      scrollBox?.addEventListener('scroll', onModalScroll, { passive: true });
      modal.addEventListener('keydown', onModalKeydown);
    }

    const initialControl = controls.find((control) => control.classList.contains('et-active_thumb')) || controls[0] || null;
    if (initialControl) {
      showMedia(initialControl);
    } else if (viewButton && mediaRecords.length) {
      viewButton.dataset.jmMediaId = mediaRecords[0].mediaId;
      viewButton.hidden = false;
    }

    const onRootClickCapture = (event) => {
      const viewButton = event.target.closest?.('[data-jm-view-larger]');
      if (viewButton && root.contains(viewButton)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        openModal(viewButton.dataset.jmMediaId, viewButton);
        return;
      }

      const control = event.target.closest?.('[data-thumbnail-id]');
      if (!control || !root.contains(control)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showMedia(control);

      if (event.isTrusted && mediaRecords.some((record) => record.mediaId === control.dataset.thumbnailId)) {
        openModal(control.dataset.thumbnailId, control);
      }
    };

    const onRootKeydown = (event) => {
      const control = event.target.closest?.('[data-thumbnail-id]');
      if (!control || !root.contains(control) || event.key !== ' ') return;

      event.preventDefault();
      event.stopPropagation();
      showMedia(control);
      if (mediaRecords.some((record) => record.mediaId === control.dataset.thumbnailId)) {
        openModal(control.dataset.thumbnailId, control);
      }
    };

    root.addEventListener('click', onRootClickCapture, true);
    root.addEventListener('keydown', onRootKeydown);

    return () => {
      root.removeEventListener('click', onRootClickCapture, true);
      root.removeEventListener('keydown', onRootKeydown);
      closeButton?.removeEventListener('click', onCloseClick);
      nextButton?.removeEventListener('click', onNextClick);
      previousButton?.removeEventListener('click', onPreviousClick);
      backdrop?.removeEventListener('click', onBackdropClick);
      scrollBox?.removeEventListener('scroll', onModalScroll);
      modal?.removeEventListener('keydown', onModalKeydown);
      imageObserver?.disconnect();
      if (scrollTimer) window.clearTimeout(scrollTimer);
      closeModal({ restoreFocus: false });
      scrollBox?.replaceChildren();
      modal?.classList.remove('jm-image-modal--pdp-2-0');
      root.removeAttribute('data-jm-media-bound');
    };
  };

  const initCommerce = (root) => {
    const mainButton = root.querySelector('#add_to_cart');
    const form = root.querySelector('form[data-product-form]');
    const stickyBar = root.querySelector('[data-jm-sticky-atc]');
    const stickyButton = stickyBar?.querySelector('[data-jm-sticky-atc-btn]') || null;
    const stickyCartIcon = stickyBar?.querySelector('[data-jm-open-cart-drawer]') || null;
    const availability = root.querySelector('[data-jm-availability]');

    if (!mainButton || !form) return;

    let mainAddInFlight = false;

    // Remove the legacy selector before jQuery ready can bind its Ajax handler.
    // The capturing listener below remains the authoritative guard if a theme-editor
    // section load binds the legacy handler before this controller runs.
    mainButton.classList.remove('et-ajax_add_to_cart_button');

    const variantSelect = form.querySelector('#ProductSelect_variant');
    const initialMainLabel = mainButton.querySelector('.et-data-add-to-cart-text')?.textContent.trim();
    const baseAddToCartLabel = window.theme?.productStrings?.addToCart
      || window.theme?.strings?.addToCart
      || (!mainButton.disabled ? initialMainLabel : '')
      || 'Add to Cart';
    const stickyAddToCartLabel = stickyButton?.textContent.trim() || 'Add to Cart';

    const selectedItem = () => {
      const quantityInput = form.querySelector('#Quantity');
      const id = Number.parseInt(variantSelect?.value || '', 10);
      const parsedQuantity = Number.parseInt(quantityInput?.value || '1', 10);

      if (!id) return null;

      return {
        id,
        quantity: parsedQuantity > 0 ? parsedQuantity : 1
      };
    };

    const setLabels = () => {
      const mainLabel = mainButton.querySelector('.et-data-add-to-cart-text');

      if (mainLabel && !mainButton.disabled) mainLabel.textContent = baseAddToCartLabel;
      if (stickyButton && !stickyButton.disabled && stickyButton.getAttribute('aria-busy') !== 'true') {
        stickyButton.textContent = stickyAddToCartLabel;
      }
    };

    const syncAvailability = () => {
      if (!availability) return;

      const item = selectedItem();
      const record = item
        ? form.querySelector(`[data-jm-variant-price-record][data-variant-id="${item.id}"]`)
        : null;
      const available = record?.getAttribute('data-available') === 'true';

      availability.textContent = available ? '✓ In stock' : 'Out of stock';
      availability.classList.toggle('jm-pdp-2-0-purchase__availability--unavailable', !available);
    };

    const applyCart = (cart) => {
      setHeaderCartCount(cart);
      setLabels();
      return cart;
    };

    const dispatchVariantChange = () => {
      window.requestAnimationFrame(() => {
        variantSelect?.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };

    if (window.jQuery) {
      window.jQuery(form)
        .off('change.jmPdp2VariantBridge', '.et-single__option')
        .on('change.jmPdp2VariantBridge', '.et-single__option', dispatchVariantChange);
    } else {
      form.addEventListener('change', (event) => {
        if (event.target?.matches('.et-single__option')) dispatchVariantChange();
      });
    }

    if (root.dataset.jmMainCartOwnerBound !== 'true') {
      root.dataset.jmMainCartOwnerBound = 'true';

      root.addEventListener('click', (event) => {
        const clickedButton = event.target.closest('#add_to_cart');
        if (!clickedButton || clickedButton !== mainButton) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (mainAddInFlight || mainButton.disabled) return;

        const item = selectedItem();
        if (!item) return;

        mainAddInFlight = true;
        mainButton.classList.add('jm-atc-loading');
        mainButton.setAttribute('aria-busy', 'true');

        const addPromise = typeof window.jmAddItems === 'function'
          ? window.jmAddItems([item], { openDrawer: true })
          : Promise.resolve(null).then(() => {
              // Fail safely to Shopify's native product form if the protected cart
              // owner is unexpectedly unavailable.
              HTMLFormElement.prototype.submit.call(form);
              return null;
            });

        Promise.resolve(addPromise)
          .then((cart) => {
            if (cart) applyCart(cart);
          })
          .catch(() => {})
          .finally(() => {
            mainAddInFlight = false;
            mainButton.classList.remove('jm-atc-loading');
            mainButton.removeAttribute('aria-busy');
          });
      }, true);
    }

    if (stickyCartIcon && stickyCartIcon.dataset.jmCartIconBound !== 'true') {
      stickyCartIcon.dataset.jmCartIconBound = 'true';
      stickyCartIcon.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof window.jmOpenCartDrawer === 'function') {
          Promise.resolve(window.jmOpenCartDrawer()).catch(() => {});
        }
      });
    }

    form.addEventListener('change', (event) => {
      if (!event.target?.matches('#ProductSelect_variant')) return;

      setLabels();
      syncAvailability();
    });
  };

  const initRoot = (root) => {
    if (!root || root.dataset.jmInitialized === 'true') return;

    root.dataset.jmInitialized = 'true';
    const layoutCleanup = initResponsiveLayout(root);
    initEmptyReviewState(root);
    initQuantity(root);
    initCopyLink(root);
    const mediaCleanup = initMediaControls(root);
    const cleanupTasks = [layoutCleanup, mediaCleanup].filter((cleanup) => typeof cleanup === 'function');
    if (cleanupTasks.length) {
      rootCleanups.set(root, () => cleanupTasks.forEach((cleanup) => cleanup()));
    }
    initCommerce(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  // This asset is loaded with defer, so the section markup is already parsed here.
  // Initialize immediately to remove the legacy Ajax selector before jQuery ready.
  initAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target.matches?.(ROOT_SELECTOR)) roots.push(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      rootCleanups.get(root)?.();
      rootCleanups.delete(root);
    });
  });
})();